import React from 'react';
import { DailyWeather } from '../../types';
import WeatherIcon from './WeatherIcon';
import { isSunnyDay } from '../../services/weatherService';

interface ThreeDayWeatherStripProps {
  forecast?: DailyWeather[];
  compact?: boolean;
  className?: string;
}

const WEEKDAYS = ['Søn', 'Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lør'];

function formatDayLabel(dateStr: string, index: number): string {
  if (index === 0) return 'I dag';
  if (index === 1) return 'I morgen';
  try {
    const d = new Date(`${dateStr}T12:00:00Z`);
    return WEEKDAYS[d.getUTCDay()] || dateStr.slice(5);
  } catch {
    return dateStr;
  }
}

export const ThreeDayWeatherStrip: React.FC<ThreeDayWeatherStripProps> = ({
  forecast,
  compact = false,
  className = '',
}) => {
  if (!forecast || forecast.length === 0) return null;

  // Vis minst 3 dager
  const daysToShow = forecast.slice(0, 3);

  return (
    <div
      className={`rounded-xl border border-[var(--border-secondary)] bg-[var(--bg-surface-alt)]/80 p-2 ${className}`}
    >
      <div className="flex items-center justify-between mb-1 px-1">
        <span className="text-4xs font-black uppercase tracking-wider text-[var(--text-muted)] flex items-center gap-1">
          <span>☀️</span> 3-dagers varsel
        </span>
      </div>

      <div className="grid grid-cols-3 gap-1.5">
        {daysToShow.map((day, idx) => {
          const isSun = isSunnyDay(day, false);
          return (
            <div
              key={day.date || idx}
              className={`flex flex-col items-center justify-center py-1.5 px-1 rounded-lg border text-center transition ${
                isSun
                  ? 'bg-amber-500/10 border-amber-500/30'
                  : 'bg-[var(--bg-surface)] border-[var(--border-primary)]'
              }`}
            >
              <span className="text-4xs font-bold uppercase tracking-tight text-[var(--text-secondary)]">
                {formatDayLabel(day.date, idx)}
              </span>

              <div className="my-0.5 flex items-center justify-center">
                <WeatherIcon symbol={day.symbol} size={compact ? 15 : 18} />
              </div>

              <div className="flex items-center gap-0.5 text-2xs font-black text-[var(--text-primary)]">
                <span>{day.tempMax}°</span>
                {!compact && typeof day.tempMin === 'number' && (
                  <span className="text-4xs font-medium text-[var(--text-muted)]">
                    /{day.tempMin}°
                  </span>
                )}
              </div>

              {day.precipMm > 0 ? (
                <span className="text-4xs font-bold text-blue-400">
                  {day.precipMm} mm
                </span>
              ) : (
                <span className="text-4xs text-emerald-400 font-medium">0 mm</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ThreeDayWeatherStrip;
